import React, { useRef, useEffect, useLayoutEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { sideBarList } from "./SidebarType";
import PimsIcon from "./PimsIcon";
import { warning } from "@alert";
import { getModuleHomePage, getSidebarNavigation } from '@helpers/checkPermission';
import { confirmPageLeaveIfDirty } from "@helpers/pageLeaveGuard";

const INDICATOR_BASE_DURATION_S = 0.28;

/** 單段 cubic-bezier 滑順彈跳；距離越遠溢量略減，避免飛過頭 */
function getIndicatorTransition(prevY, nextY) {
  const distance = Math.abs(nextY - prevY);
  const duration = INDICATOR_BASE_DURATION_S + Math.min(distance / 900, 0.06);
  const y2 = Math.max(1.12, 1.38 - distance / 400);
  return `transform ${duration}s cubic-bezier(0.34, ${y2.toFixed(3)}, 0.64, 1)`;
}

const Sidebar = ({ routerName }) => {
  const permissions = useSelector(state => state.user.user.permissions);
  const navigate = useNavigate();
  const sidebarRef = useRef(null);
  const menuListRef = useRef(null);
  const itemRefs = useRef([]);
  const settledYRef = useRef(null);
  const isAnimatingRef = useRef(false);
  const [indicatorY, setIndicatorY] = useState(null);
  const [indicatorTransition, setIndicatorTransition] = useState("none");

  const activeIndex = sideBarList.findIndex((item) => item.name === routerName);
  const activeItem = activeIndex >= 0 ? sideBarList[activeIndex] : null;
  const { allowed: activeAllowed } = activeItem
    ? getSidebarNavigation(activeItem.name, permissions)
    : { allowed: false };

  const measureTargetY = useCallback(() => {
    if (activeIndex < 0 || !activeAllowed) return null;

    const el = itemRefs.current[activeIndex];
    const sidebarEl = sidebarRef.current;
    if (!el || !sidebarEl) return null;

    const menuEl = el.querySelector(".sidebar__menu");
    if (!menuEl) return null;

    const sidebarRect = sidebarEl.getBoundingClientRect();
    const menuRect = menuEl.getBoundingClientRect();
    return menuRect.top - sidebarRect.top;
  }, [activeIndex, activeAllowed]);

  const applyIdlePosition = useCallback((y) => {
    settledYRef.current = y;
    isAnimatingRef.current = false;
    setIndicatorTransition("none");
    setIndicatorY(y);
  }, []);

  const clearIndicator = useCallback(() => {
    settledYRef.current = null;
    isAnimatingRef.current = false;
    setIndicatorTransition("none");
    setIndicatorY(null);
  }, []);

  const syncLayoutPosition = useCallback(() => {
    if (isAnimatingRef.current) return;

    const nextY = measureTargetY();
    if (nextY == null) {
      clearIndicator();
      return;
    }

    if (settledYRef.current == null || Math.abs(nextY - settledYRef.current) > 0.5) {
      applyIdlePosition(nextY);
    }
  }, [measureTargetY, applyIdlePosition, clearIndicator]);

  // 路由切換：單段 transition 滑順彈跳（useLayoutEffect：首屏繪製前就定位，避免「剛登入」閃錯位置）
  useLayoutEffect(() => {
    let rafId = 0;
    let rafId2 = 0;

    const startRouteAnimation = (prevY, nextY) => {
      // 必須同步設 flag，否則 remeasure / ResizeObserver 會在同一幀搶先 applyIdlePosition 而跳過動畫
      isAnimatingRef.current = true;
      settledYRef.current = nextY;
      const transition = getIndicatorTransition(prevY, nextY);

      setIndicatorTransition("none");
      setIndicatorY(prevY);
      rafId2 = requestAnimationFrame(() => {
        setIndicatorTransition(transition);
        setIndicatorY(nextY);
      });
    };

    const run = () => {
      const nextY = measureTargetY();
      if (nextY == null) {
        if (activeIndex < 0 || !activeAllowed) {
          clearIndicator();
        } else {
          rafId = requestAnimationFrame(run);
        }
        return;
      }

      const prevY = settledYRef.current;
      if (prevY != null && Math.abs(nextY - prevY) > 0.5) {
        startRouteAnimation(prevY, nextY);
      } else {
        applyIdlePosition(nextY);
      }
    };

    // 首屏／路由切換時 DOM 已就緒，同步量測並決定是否動畫，避免等 raf 才被 remeasure 搶跑
    const nextY = measureTargetY();
    if (nextY != null) {
      const prevY = settledYRef.current;
      if (prevY != null && Math.abs(nextY - prevY) > 0.5) {
        startRouteAnimation(prevY, nextY);
      } else {
        applyIdlePosition(nextY);
      }
    } else if (activeIndex < 0 || !activeAllowed) {
      clearIndicator();
    } else {
      rafId = requestAnimationFrame(run);
    }

    return () => {
      cancelAnimationFrame(rafId);
      cancelAnimationFrame(rafId2);
    };
  }, [routerName, measureTargetY, applyIdlePosition, clearIndicator, activeIndex, activeAllowed]);

  // 字型與子像素排版穩定後再校正（剛登入時常晚一幀／等 webfont）
  useEffect(() => {
    let cancelled = false;
    const remeasure = () => {
      if (cancelled) return;
      syncLayoutPosition();
    };
    const scheduleDoubleRafRemeasure = () => {
      requestAnimationFrame(() => {
        requestAnimationFrame(remeasure);
      });
    };

    scheduleDoubleRafRemeasure();

    document.fonts?.ready?.then(() => {
      if (!cancelled) remeasure();
    });

    const timeout = window.setTimeout(remeasure, 120);

    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [routerName, syncLayoutPosition, activeAllowed, activeIndex]);

  useEffect(() => {
    const listEl = menuListRef.current;
    if (!listEl) return undefined;

    const ro = typeof ResizeObserver !== "undefined"
      ? new ResizeObserver(syncLayoutPosition)
      : null;

    const sidebarEl = sidebarRef.current;

    ro?.observe(listEl);
    if (sidebarEl) ro.observe(sidebarEl);
    itemRefs.current.forEach((el) => el && ro?.observe(el));

    listEl.addEventListener("scroll", syncLayoutPosition, { passive: true });
    window.addEventListener("resize", syncLayoutPosition);

    return () => {
      ro?.disconnect();
      listEl.removeEventListener("scroll", syncLayoutPosition);
      window.removeEventListener("resize", syncLayoutPosition);
    };
  }, [syncLayoutPosition]);

  const handleIndicatorTransitionEnd = (event) => {
    if (event.propertyName !== "transform") return;
    isAnimatingRef.current = false;
    setIndicatorTransition("none");
  };

  return (
    <div ref={sidebarRef} className="sidebar d-flex flex-column align-items-center">
      {indicatorY != null && (
        <div
          className="sidebar__indicator"
          style={{
            transform: `translateY(${indicatorY}px)`,
            transition: indicatorTransition,
          }}
          onTransitionEnd={handleIndicatorTransitionEnd}
          aria-hidden
        />
      )}
      <div
        className="sidebar__logo-wrap cursor-pointer"
        onClick={() =>
          confirmPageLeaveIfDirty(() =>
            navigate(getModuleHomePage(routerName, permissions))
          )
        }
      >
        <img
          src={process.env.PUBLIC_URL + `/assets/img/sidebar/${process.env.REACT_APP_MODAL}-sidebar-logo.png`}
          srcSet={`${process.env.PUBLIC_URL}/assets/img/sidebar/${process.env.REACT_APP_MODAL}-sidebar-logo@2x.png 2x`}
          alt="logo"
          className="sidebar__logo"
          onLoad={() => {
            // Logo 高度載入後才把下方選單推到最終位置；僅 Resize 選單內部不一定會觸發
            syncLayoutPosition();
          }}
        />
      </div>
      <div
        ref={menuListRef}
        className="sidebar__menu-list position-relative"
      >
        {sideBarList.map((sidebarItem, index) => {

          const isActiveRoute = routerName === sidebarItem.name;
          const { allowed: sidebarNavAllowed } = getSidebarNavigation(
            sidebarItem.name,
            permissions
          );
          const showSelectedStyle = isActiveRoute && sidebarNavAllowed;

          const handleSidebarClick = () => {
            const { allowed, path } = getSidebarNavigation(sidebarItem.name, permissions);

            if (!allowed) {
              warning(1,
                "權限不足 !",
                "請使用有更高權限的帳號進行此操作",
                "我了解了"
              );
              return;
            }

            confirmPageLeaveIfDirty(() =>
              navigate(path || sidebarItem.path)
            );
          };

          const isSmallIcon = sidebarItem.name === "statistics" || sidebarItem.name === "sales";

          const icon = sidebarItem.name === "pims" ?
            <PimsIcon size={24} isActive={showSelectedStyle} noPermission={!sidebarNavAllowed} /> :
            sidebarItem.icon;

          return (
            <div
              key={sidebarItem.name}
              ref={(el) => { itemRefs.current[index] = el; }}
              className={`d-flex flex-column align-items-center cursor-pointer${
                sidebarItem.name === "pims" ? " sidebar__item--pims" : ""
              }${!sidebarNavAllowed ? " sidebar__item--no-permission" : ""}`}
              onClick={handleSidebarClick}
            >
              <div
                className={`d-flex flex-column align-items-center sidebar__menu${
                  showSelectedStyle ? " sidebar__target" : ""
                }`}
              >
                <div className={`sidebar__icon${isSmallIcon ? " sidebar__icon--sm" : ""}`}>
                  {icon}
                </div>
                <p className="mb-0 sidebar__menu_text">
                  {sidebarItem.text}
                </p>
              </div>
            </div>
          );
        })}
      </div>
      <div className="sidebar__version">
        <span>v7.18.19</span>
        <span>2026-10-15</span>
      </div>
    </div>
  );
};

export default Sidebar;
